import React from "react";
import axios from "axios";
import { formatInTimeZone } from "date-fns-tz";
import {
  MdArrowBack,
  MdCheckCircle,
  MdEdit,
  MdLock,
  MdOutlineRefresh,
  MdWarningAmber,
} from "react-icons/md";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";
import LessonConfirmationPayroll from "./LessonConfirmationPayroll";
import LessonConfirmationRescheduling from "./LessonConfirmationRescheduling";
import LessonConfirmationPilotMonitoring from "./LessonConfirmationPilotMonitoring";
import "./lesson-confirmation-admin.css";

export const NOT_TAUGHT_REASONS = [
  {
    value: "student_cancelled_more_than_24_hours",
    label: "Student cancelled more than 24 hours ahead",
  },
  {
    value: "student_cancelled_less_than_24_hours",
    label: "Student cancelled less than 24 hours ahead",
  },
  { value: "teacher_cancelled", label: "Teacher cancelled" },
];

const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}$/;

const localDateValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const defaultDates = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { fromDate: localDateValue(start), toDate: localDateValue(end) };
};

const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const answerLabel = (answer) => {
  if (answer === "taught") return "Yes — taught";
  if (answer === "not_taught") return "No — not taught";
  return titleCase(answer) || "Pending";
};

const reasonLabel = (reason) =>
  NOT_TAUGHT_REASONS.find((option) => option.value === reason)?.label ||
  titleCase(reason) ||
  "—";

const formatDate = (value, timeZone, pattern = "MMM d, yyyy · h:mm a zzz") => {
  if (!value) return "—";
  try {
    return formatInTimeZone(new Date(value), timeZone || "UTC", pattern);
  } catch (_error) {
    return "—";
  }
};

const formatServiceDate = (value) => {
  if (!DATE_FORMAT.test(String(value || ""))) return value || "—";
  return new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatMoney = (cents) =>
  Number.isInteger(cents)
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(cents / 100)
    : "Needs review";

const normalizeRequestError = (error, fallback) => ({
  status: error?.response?.status || null,
  code: error?.response?.data?.code || "",
  message:
    error?.response?.data?.error ||
    error?.message ||
    fallback,
});

const StatusChip = ({ children, tone = "neutral" }) => (
  <span className={`lesson-confirmations__chip lesson-confirmations__chip--${tone}`}>
    {children}
  </span>
);

const ErrorNotice = ({ error, onRetry }) => {
  if (!error) return null;
  return (
    <div className="lesson-confirmations__error" role="alert">
      <MdWarningAmber aria-hidden="true" />
      <div>
        <strong>
          {error.status === 403 ? "Administrator access required" : "Unable to continue"}
        </strong>
        {error.code && <code>{error.code}</code>}
        <p>{error.message}</p>
      </div>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
};

const ListSkeleton = () => (
  <div className="lesson-confirmations__table-card" aria-label="Loading confirmation days">
    <div className="lesson-confirmations__skeleton-summary">
      {[1, 2, 3].map((item) => (
        <span key={item} />
      ))}
    </div>
    {[1, 2, 3, 4, 5].map((row) => (
      <div className="lesson-confirmations__skeleton-row" key={row}>
        {[1, 2, 3, 4, 5].map((cell) => (
          <span key={cell} />
        ))}
      </div>
    ))}
  </div>
);

const SummaryCards = ({ summary }) => (
  <div className="lesson-confirmations__summary" aria-label="Filtered confirmation summary">
    <div>
      <span>Submitted days</span>
      <strong>{summary?.filtered || 0}</strong>
    </div>
    <div className={(summary?.needsReview || 0) > 0 ? "attention" : ""}>
      <span>Needs review</span>
      <strong>{summary?.needsReview || 0}</strong>
    </div>
    <div>
      <span>Corrected days</span>
      <strong>{summary?.corrected || 0}</strong>
    </div>
    <div>
      <span>Demo days</span>
      <strong>{summary?.demo || 0}</strong>
    </div>
  </div>
);

const ReviewList = ({ onSelectBatch, refreshToken }) => {
  const dates = React.useMemo(defaultDates, []);
  const [filters, setFilters] = React.useState({
    ...dates,
    teacherId: "",
    status: "submitted,locked",
    reviewStatus: "all",
    dataType: "real",
  });
  const [cursor, setCursor] = React.useState("");
  const [cursorHistory, setCursorHistory] = React.useState([]);
  const [result, setResult] = React.useState({
    items: [],
    summary: {},
    filterOptions: { teachers: [] },
    nextCursor: null,
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const loadDays = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${baseUrl}/native/lesson-confirmations/admin/days`,
        {
          ...getRequestHeaders(),
          params: {
            ...filters,
            limit: 50,
            ...(cursor ? { cursor } : {}),
          },
        }
      );
      setResult({
        items: Array.isArray(response.data?.items) ? response.data.items : [],
        summary: response.data?.summary || {},
        filterOptions: response.data?.filterOptions || { teachers: [] },
        nextCursor: response.data?.nextCursor || null,
      });
    } catch (requestError) {
      setError(
        normalizeRequestError(
          requestError,
          "Unable to load submitted lesson confirmations."
        )
      );
    } finally {
      setLoading(false);
    }
  }, [cursor, filters]);

  React.useEffect(() => {
    loadDays();
  }, [loadDays, refreshToken]);

  const updateFilter = (key, value) => {
    setCursor("");
    setCursorHistory([]);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const goNext = () => {
    if (!result.nextCursor) return;
    setCursorHistory((history) => [...history, cursor]);
    setCursor(result.nextCursor);
  };

  const goPrevious = () => {
    setCursorHistory((history) => {
      if (!history.length) return history;
      setCursor(history[history.length - 1] || "");
      return history.slice(0, -1);
    });
  };

  return (
    <>
      <header className="lesson-confirmations__header">
        <div>
          <p className="lesson-confirmations__eyebrow">Teacher administration</p>
          <h1>Lesson Confirmations</h1>
          <p>Review submitted lesson answers and make fully audited corrections.</p>
        </div>
        <button
          type="button"
          className="lesson-confirmations__button lesson-confirmations__button--secondary"
          onClick={loadDays}
          disabled={loading}
        >
          <MdOutlineRefresh aria-hidden="true" />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      <section className="lesson-confirmations__filters" aria-label="Confirmation filters">
        <label>
          From
          <input
            type="date"
            value={filters.fromDate}
            onChange={(event) => updateFilter("fromDate", event.target.value)}
          />
        </label>
        <label>
          To
          <input
            type="date"
            value={filters.toDate}
            onChange={(event) => updateFilter("toDate", event.target.value)}
          />
        </label>
        <label>
          Teacher
          <select
            value={filters.teacherId}
            onChange={(event) => updateFilter("teacherId", event.target.value)}
          >
            <option value="">All teachers</option>
            {(result.filterOptions?.teachers || []).map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {teacher.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Submission status
          <select
            value={filters.status}
            onChange={(event) => updateFilter("status", event.target.value)}
          >
            <option value="submitted,locked">Submitted and locked</option>
            <option value="submitted">Submitted</option>
            <option value="locked">Locked</option>
          </select>
        </label>
        <label>
          Review status
          <select
            value={filters.reviewStatus}
            onChange={(event) => updateFilter("reviewStatus", event.target.value)}
          >
            <option value="all">All review states</option>
            <option value="needs_review">Needs review</option>
            <option value="has_corrections">Has corrections</option>
            <option value="no_corrections">No corrections</option>
          </select>
        </label>
        <label>
          Data
          <select
            value={filters.dataType}
            onChange={(event) => updateFilter("dataType", event.target.value)}
          >
            <option value="real">Real data only</option>
            <option value="demo">Demo data only</option>
            <option value="all">Real and demo</option>
          </select>
        </label>
      </section>

      {!loading && !error && <SummaryCards summary={result.summary} />}
      <ErrorNotice error={error} onRetry={loadDays} />
      {loading ? (
        <ListSkeleton />
      ) : !error && result.items.length === 0 ? (
        <div className="lesson-confirmations__empty">
          <MdCheckCircle aria-hidden="true" />
          <h2>No submitted days match these filters</h2>
          <p>Try a wider date range or a different review status.</p>
        </div>
      ) : !error ? (
        <div className="lesson-confirmations__table-card">
          <div className="lesson-confirmations__table-scroll">
            <table className="lesson-confirmations__table">
              <thead>
                <tr>
                  <th>Teacher</th>
                  <th>Service date</th>
                  <th>Submitted</th>
                  <th>Lessons</th>
                  <th>Answers</th>
                  <th>Review</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((day) => (
                  <tr key={day.id} className={day.isDemo ? "is-demo" : ""}>
                    <td>
                      <strong>{day.teacher?.name || day.teacher?.id}</strong>
                      <div className="lesson-confirmations__cell-chips">
                        {day.isDemo && <StatusChip tone="demo">Demo</StatusChip>}
                        <StatusChip tone={day.status === "locked" ? "locked" : "success"}>
                          {day.status === "locked" && <MdLock aria-hidden="true" />}
                          {titleCase(day.status)}
                        </StatusChip>
                      </div>
                    </td>
                    <td>{formatServiceDate(day.serviceDate)}</td>
                    <td>{formatDate(day.submittedAt, day.timeZone)}</td>
                    <td><strong>{day.counts?.lessons || 0}</strong></td>
                    <td>
                      <span>{day.counts?.taught || 0} taught</span>
                      <small>{day.counts?.cancelled || 0} cancelled</small>
                    </td>
                    <td>
                      {(day.counts?.needsReview || 0) > 0 ? (
                        <StatusChip tone="warning">
                          {day.counts.needsReview} need review
                        </StatusChip>
                      ) : (
                        <StatusChip tone="success">Ready</StatusChip>
                      )}
                      {(day.counts?.corrected || 0) > 0 && (
                        <small>{day.counts.corrected} corrected</small>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="lesson-confirmations__row-button"
                        onClick={() => onSelectBatch(day.id)}
                        aria-label={`Review ${day.teacher?.name || day.teacher?.id} on ${day.serviceDate}`}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="lesson-confirmations__pagination">
            <span>Showing up to 50 submitted days</span>
            <div>
              <button type="button" onClick={goPrevious} disabled={!cursorHistory.length}>
                Previous
              </button>
              <button type="button" onClick={goNext} disabled={!result.nextCursor}>
                Next
              </button>
            </div>
          </footer>
        </div>
      ) : null}
    </>
  );
};

const ValueChange = ({ label, previous, proposed, money = false }) => {
  const render = (value) => (money ? formatMoney(value) : String(value ?? "—"));
  const changed = previous !== proposed;
  return (
    <div className={changed ? "changed" : ""}>
      <span>{label}</span>
      <p>
        <strong>{render(previous)}</strong>
        <span aria-hidden="true">→</span>
        <strong>{render(proposed)}</strong>
      </p>
    </div>
  );
};

const CorrectionModal = ({ occurrence, batch, teacher, onClose, onSaved }) => {
  const initialTaught = occurrence.confirmationStatus === "taught";
  const [answer, setAnswer] = React.useState(initialTaught ? "yes" : "no");
  const [notTaughtReason, setNotTaughtReason] = React.useState(
    occurrence.notTaughtReason || NOT_TAUGHT_REASONS[0].value
  );
  const [correctionReason, setCorrectionReason] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose]);

  const answerPayload = {
    answer,
    ...(answer === "no" ? { notTaughtReason } : {}),
    expectedVersion: occurrence.version,
  };
  const endpoint =
    `${baseUrl}/native/lesson-confirmations/admin/teachers/` +
    `${encodeURIComponent(teacher.id)}/${encodeURIComponent(batch.serviceDate)}` +
    `/lessons/${encodeURIComponent(occurrence.id)}`;

  const requestPreview = async () => {
    if (!correctionReason.trim()) {
      setError({ message: "Enter a written reason for this administrator correction." });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await axios.post(
        `${endpoint}/correction-preview`,
        answerPayload,
        getRequestHeaders()
      );
      setPreview(response.data);
    } catch (requestError) {
      setError(normalizeRequestError(requestError, "Unable to preview this correction."));
    } finally {
      setBusy(false);
    }
  };

  const saveCorrection = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.patch(
        endpoint,
        { ...answerPayload, correctionReason: correctionReason.trim() },
        getRequestHeaders()
      );
      await onSaved();
    } catch (requestError) {
      const normalized = normalizeRequestError(
        requestError,
        "Unable to save this correction."
      );
      setError(normalized);
      if (normalized.code === "LESSON_CONFIRMATION_STALE") setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const changed =
    preview && JSON.stringify(preview.previous) !== JSON.stringify(preview.proposed);

  return (
    <div className="lesson-confirmations__modal-backdrop" onMouseDown={busy ? undefined : onClose}>
      <section
        className="lesson-confirmations__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lesson-correction-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <p className="lesson-confirmations__eyebrow">Administrator correction</p>
            <h2 id="lesson-correction-title">{occurrence.studentName}</h2>
            <p>{formatDate(occurrence.scheduledStartAt, batch.timeZone, "EEEE, MMM d · h:mm a")}</p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close correction dialog">×</button>
        </header>

        {!preview ? (
          <div className="lesson-confirmations__modal-body">
            <div className="lesson-confirmations__original-callout">
              <strong>Teacher’s original answer</strong>
              <span>{answerLabel(occurrence.originalTeacherAnswer?.confirmationStatus)}</span>
              {occurrence.originalTeacherAnswer?.notTaughtReason && (
                <small>{reasonLabel(occurrence.originalTeacherAnswer.notTaughtReason)}</small>
              )}
            </div>
            <fieldset>
              <legend>Correct answer</legend>
              <label className="lesson-confirmations__radio">
                <input
                  type="radio"
                  name="corrected-answer"
                  value="yes"
                  checked={answer === "yes"}
                  onChange={() => setAnswer("yes")}
                />
                Yes — lesson was taught
              </label>
              <label className="lesson-confirmations__radio">
                <input
                  type="radio"
                  name="corrected-answer"
                  value="no"
                  checked={answer === "no"}
                  onChange={() => setAnswer("no")}
                />
                No — lesson was not taught
              </label>
            </fieldset>
            {answer === "no" && (
              <label>
                Not-taught reason
                <select value={notTaughtReason} onChange={(event) => setNotTaughtReason(event.target.value)}>
                  {NOT_TAUGHT_REASONS.map((reason) => (
                    <option key={reason.value} value={reason.value}>{reason.label}</option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Written correction reason <span aria-hidden="true">*</span>
              <textarea
                value={correctionReason}
                onChange={(event) => setCorrectionReason(event.target.value)}
                maxLength={1000}
                rows={4}
                placeholder="Explain why the submitted teacher answer must be corrected."
              />
              <small>This reason becomes part of the permanent audit history.</small>
            </label>
          </div>
        ) : (
          <div className="lesson-confirmations__modal-body">
            <div className="lesson-confirmations__preview-warning">
              <MdWarningAmber aria-hidden="true" />
              <div>
                <strong>Confirm the authoritative server changes</strong>
                <p>No change has been saved yet.</p>
              </div>
            </div>
            <div className="lesson-confirmations__changes">
              <ValueChange label="Answer" previous={answerLabel(preview.previous?.confirmationStatus)} proposed={answerLabel(preview.proposed?.confirmationStatus)} />
              <ValueChange label="Pay status" previous={titleCase(preview.previous?.teacherPayStatus)} proposed={titleCase(preview.proposed?.teacherPayStatus)} />
              <ValueChange label="Payable minutes" previous={preview.previous?.payableMinutes} proposed={preview.proposed?.payableMinutes} />
              <ValueChange label="Base pay" previous={preview.previous?.basePayCents} proposed={preview.proposed?.basePayCents} money />
              <ValueChange label="Bonus eligible" previous={preview.previous?.bonusEligible ? "Yes" : "No"} proposed={preview.proposed?.bonusEligible ? "Yes" : "No"} />
              <ValueChange label="Rescheduling" previous={titleCase(preview.previous?.rescheduleStatus)} proposed={titleCase(preview.proposed?.rescheduleStatus)} />
            </div>
            {preview.effects?.payStatementWillBeInvalidated && (
              <p className="lesson-confirmations__invalidation">
                Saving will return the related pay statement to draft so the backend can recalculate it.
              </p>
            )}
            {!changed && (
              <p className="lesson-confirmations__no-change">Choose a different answer or reason; this would not change the saved record.</p>
            )}
            <div className="lesson-confirmations__audit-reason">
              <span>Audit reason</span>
              <p>{correctionReason}</p>
            </div>
          </div>
        )}

        <ErrorNotice error={error} />
        <footer>
          {preview ? (
            <button type="button" className="lesson-confirmations__button lesson-confirmations__button--secondary" onClick={() => { setPreview(null); setError(null); }} disabled={busy}>
              Back
            </button>
          ) : (
            <button type="button" className="lesson-confirmations__button lesson-confirmations__button--secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className="lesson-confirmations__button lesson-confirmations__button--danger"
            onClick={preview ? saveCorrection : requestPreview}
            disabled={busy || !correctionReason.trim() || (preview && !changed)}
          >
            {busy ? "Working…" : preview ? "Save audited correction" : "Review correction"}
          </button>
        </footer>
      </section>
    </div>
  );
};

const OccurrenceRow = ({ occurrence, batch, canCorrect, onCorrect }) => {
  const original = occurrence.originalTeacherAnswer || {};
  return (
    <article className={`lesson-confirmations__occurrence ${occurrence.corrected ? "is-corrected" : ""}`}>
      <div className="lesson-confirmations__occurrence-main">
        <div>
          <strong>{occurrence.studentName || "Unknown student"}</strong>
          <span>
            {formatDate(occurrence.scheduledStartAt, batch.timeZone, "h:mm a")}–{formatDate(occurrence.scheduledEndAt, batch.timeZone, "h:mm a")}
            {` · ${occurrence.durationMinutes} min`}
          </span>
        </div>
        <div className="lesson-confirmations__cell-chips">
          <StatusChip>{titleCase(occurrence.lessonType)}</StatusChip>
          {occurrence.corrected && <StatusChip tone="corrected">Corrected</StatusChip>}
          {occurrence.teacherPayStatus === "needs_review" && <StatusChip tone="warning">Needs review</StatusChip>}
        </div>
      </div>
      <div className="lesson-confirmations__occurrence-grid">
        <div>
          <span>Teacher’s original answer</span>
          <strong>{answerLabel(original.confirmationStatus)}</strong>
          {original.notTaughtReason && <small>{reasonLabel(original.notTaughtReason)}</small>}
          <small>
            {original.answeredByRole === "teacher"
              ? occurrence.teacherName || original.answeredById || occurrence.teacherId
              : original.answeredById || occurrence.teacherId}
            {original.answeredAt
              ? ` · answered ${formatDate(original.answeredAt, batch.timeZone)}`
              : ` · submitted ${formatDate(original.submittedAt, batch.timeZone)}`}
          </small>
        </div>
        <div>
          <span>Current authoritative answer</span>
          <strong>{answerLabel(occurrence.confirmationStatus)}</strong>
          {occurrence.notTaughtReason && <small>{reasonLabel(occurrence.notTaughtReason)}</small>}
          <small>
            {occurrence.answeredByRole === "teacher"
              ? occurrence.teacherName || occurrence.answeredById || "Teacher"
              : occurrence.answeredById || titleCase(occurrence.answeredByRole)}
            {` · ${formatDate(occurrence.answeredAt, batch.timeZone)}`}
          </small>
        </div>
        <div>
          <span>Payment</span>
          <strong>{titleCase(occurrence.teacherPayStatus)}</strong>
          <small>{occurrence.payableMinutes} payable min · {formatMoney(occurrence.basePayCents)}</small>
        </div>
        <div>
          <span>Bonus / rescheduling</span>
          <strong className={occurrence.bonusEligible ? "bonus" : ""}>{occurrence.bonusEligible ? "Bonus eligible" : "Not bonus eligible"}</strong>
          <small>{titleCase(occurrence.rescheduleStatus)}</small>
        </div>
      </div>
      <div className="lesson-confirmations__occurrence-footer">
        {occurrence.answerRevisions?.length > 0 ? (
          <details>
            <summary>{occurrence.answerRevisions.length} correction {occurrence.answerRevisions.length === 1 ? "record" : "records"}</summary>
            <ol>
              {occurrence.answerRevisions.map((revision) => (
                <li key={revision.id}>
                  <strong>{formatDate(revision.changedAt, batch.timeZone)}</strong>
                  <p>{revision.reason}</p>
                  <div className="lesson-confirmations__history-values">
                    <div>
                      <span>Previous</span>
                      <small>{answerLabel(revision.previous?.confirmationStatus)}</small>
                      <small>{titleCase(revision.previous?.teacherPayStatus)} · {revision.previous?.payableMinutes} payable min</small>
                      <small>{revision.previous?.bonusEligible ? "Bonus eligible" : "Not bonus eligible"} · {titleCase(revision.previous?.rescheduleStatus)}</small>
                    </div>
                    <div>
                      <span>New</span>
                      <small>{answerLabel(revision.next?.confirmationStatus)}</small>
                      <small>{titleCase(revision.next?.teacherPayStatus)} · {revision.next?.payableMinutes} payable min</small>
                      <small>{revision.next?.bonusEligible ? "Bonus eligible" : "Not bonus eligible"} · {titleCase(revision.next?.rescheduleStatus)}</small>
                    </div>
                  </div>
                  <small>Corrected by {revision.changedById}</small>
                </li>
              ))}
            </ol>
          </details>
        ) : <span>No administrator corrections</span>}
        {canCorrect && (
          <button type="button" onClick={() => onCorrect(occurrence)}>
            <MdEdit aria-hidden="true" /> Correct answer
          </button>
        )}
      </div>
    </article>
  );
};

const ReviewDetail = ({ batchId, onBack, onChanged }) => {
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [correctionOccurrence, setCorrectionOccurrence] = React.useState(null);

  const loadDetail = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${baseUrl}/native/lesson-confirmations/admin/days/${encodeURIComponent(batchId)}`,
        getRequestHeaders()
      );
      setDetail(response.data);
    } catch (requestError) {
      setError(normalizeRequestError(requestError, "Unable to load this confirmation day."));
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  React.useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleSaved = async () => {
    setCorrectionOccurrence(null);
    onChanged();
    await loadDetail();
  };

  if (loading && !detail) return <div className="lesson-confirmations__detail-loading"><ListSkeleton /></div>;

  return (
    <>
      <button type="button" className="lesson-confirmations__back" onClick={onBack}>
        <MdArrowBack aria-hidden="true" /> Back to submitted days
      </button>
      <ErrorNotice error={error} onRetry={loadDetail} />
      {detail && (
        <>
          <header className="lesson-confirmations__detail-header">
            <div>
              <div className="lesson-confirmations__detail-title">
                <h1>{detail.teacher?.name || detail.teacher?.id}</h1>
                {detail.batch?.isDemo && <StatusChip tone="demo">Demo data</StatusChip>}
                <StatusChip tone={detail.batch?.status === "locked" ? "locked" : "success"}>
                  {titleCase(detail.batch?.status)}
                </StatusChip>
              </div>
              <p>{formatServiceDate(detail.batch?.serviceDate)} · {detail.batch?.timeZone}</p>
              <small>Submitted {formatDate(detail.batch?.submittedAt, detail.batch?.timeZone)}</small>
            </div>
            <button type="button" className="lesson-confirmations__button lesson-confirmations__button--secondary" onClick={loadDetail} disabled={loading}>
              <MdOutlineRefresh aria-hidden="true" /> {loading ? "Refreshing…" : "Refresh"}
            </button>
          </header>

          {detail.batch?.isDemo && (
            <div className="lesson-confirmations__demo-notice">
              <strong>Demo confirmation day</strong>
              <span>All generated lessons remain excluded from payroll, bonuses, and make-up reporting.</span>
            </div>
          )}
          {!detail.batch?.correction?.canCorrect && (
            <div className="lesson-confirmations__locked-notice">
              <MdLock aria-hidden="true" />
              <div>
                <strong>Corrections are unavailable</strong>
                <span>{detail.batch?.correction?.blockedReason || "This day cannot be corrected."}</span>
              </div>
            </div>
          )}

          <section className="lesson-confirmations__occurrences" aria-label="Submitted lesson answers">
            {(detail.items || []).map((occurrence) => (
              <OccurrenceRow
                key={occurrence.id}
                occurrence={occurrence}
                batch={detail.batch}
                canCorrect={Boolean(detail.batch?.correction?.canCorrect)}
                onCorrect={setCorrectionOccurrence}
              />
            ))}
          </section>
          {detail.items?.length === 0 && (
            <div className="lesson-confirmations__empty"><h2>No lesson occurrences</h2></div>
          )}
        </>
      )}
      {correctionOccurrence && detail && (
        <CorrectionModal
          occurrence={correctionOccurrence}
          batch={detail.batch}
          teacher={detail.teacher}
          onClose={() => setCorrectionOccurrence(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};

const LessonConfirmationAdmin = ({ userData }) => {
  const [activeSection, setActiveSection] = React.useState("review");
  const [selectedBatchId, setSelectedBatchId] = React.useState("");
  const [refreshToken, setRefreshToken] = React.useState(0);

  if (!userData?.IsAdmin) return null;

  return (
    <main className="lesson-confirmations">
      <nav className="lesson-confirmations__area-tabs" aria-label="Lesson confirmation administration">
        <button
          type="button"
          className={activeSection === "review" ? "active" : ""}
          aria-current={activeSection === "review" ? "page" : undefined}
          onClick={() => setActiveSection("review")}
        >
          Confirmation Review
        </button>
        <button
          type="button"
          className={activeSection === "payroll" ? "active" : ""}
          aria-current={activeSection === "payroll" ? "page" : undefined}
          onClick={() => {
            setSelectedBatchId("");
            setActiveSection("payroll");
          }}
        >
          Monthly Payroll
        </button>
        <button
          type="button"
          className={activeSection === "rescheduling" ? "active" : ""}
          aria-current={activeSection === "rescheduling" ? "page" : undefined}
          onClick={() => {
            setSelectedBatchId("");
            setActiveSection("rescheduling");
          }}
        >
          Make-ups &amp; Rescheduling
        </button>
        <button
          type="button"
          className={activeSection === "pilot" ? "active" : ""}
          aria-current={activeSection === "pilot" ? "page" : undefined}
          onClick={() => {
            setSelectedBatchId("");
            setActiveSection("pilot");
          }}
        >
          Pilot Monitoring
        </button>
      </nav>
      {activeSection === "pilot" ? (
        <LessonConfirmationPilotMonitoring userData={userData} />
      ) : activeSection === "rescheduling" ? (
        <LessonConfirmationRescheduling userData={userData} />
      ) : activeSection === "payroll" ? (
        <LessonConfirmationPayroll userData={userData} />
      ) : selectedBatchId ? (
        <ReviewDetail
          batchId={selectedBatchId}
          onBack={() => setSelectedBatchId("")}
          onChanged={() => setRefreshToken((token) => token + 1)}
        />
      ) : (
        <ReviewList onSelectBatch={setSelectedBatchId} refreshToken={refreshToken} />
      )}
    </main>
  );
};

export default LessonConfirmationAdmin;
